<template>
  <v-container fluid>
    <v-row justify="center" align="center">
      <v-col cols="12" sm="12" md="6" lg="6">
        <v-fade-transition mode="out-in">
          <v-card
            v-if="!searchResult"
            key="login-form"
            elevation="10"
            class="rounded-lg pa-4"
          >
            <v-card-title
              class="text-center text-h5 text-indigo-darken-4 font-weight-bold mb-4"
            >
              <v-icon icon="mdi-shield-account" start></v-icon>
              ชมรมศิษย์เก่า NPTU
            </v-card-title>

            <v-card-text>
              <v-form @submit.prevent="checkStatus">
                <v-text-field
                  v-model="form.studentNum"
                  label="รหัสนักศึกษา"
                  prepend-inner-icon="mdi-card-account-details"
                  variant="outlined"
                  :rules="[(v) => !!v || 'กรุณากรอกรหัสนักศึกษา']"
                  required
                ></v-text-field>

                <v-text-field
                  v-model="form.phone"
                  label="เบอร์โทรศัพท์ (รหัสผ่าน)"
                  prepend-inner-icon="mdi-phone"
                  variant="outlined"
                  type="tel"
                  :rules="[(v) => !!v || 'กรุณากรอกเบอร์โทรศัพท์']"
                  required
                  class="mt-2"
                ></v-text-field>
                <v-row justify="center">
                  <v-col cols="12" md="6">
                    <v-btn
                      block
                      color="#EC407A"
                      type="submit"
                      :loading="isLoading"
                      size="large"
                      class="text-white"
                    >
                      Login
                    </v-btn>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-btn
                      block
                      color="#1A237E"
                      class="text-white"
                      type="submit"
                      to="/alumni"
                      size="large"
                    >
                      ลงทะเบียนสมาชิกชมรมศิษย์เก่า
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>

          <v-card
            v-else
            key="result-card"
            elevation="10"
            class="rounded-lg pa-4 border-t-8"
            :class="`border-${statusColor(searchResult.status)}`"
          >
            <v-card-title class="d-flex align-center justify-start">
              <span class="text-h6 font-weight-bold">ผลการตรวจสอบ</span>
              <v-chip
                :color="statusColor(searchResult.status)"
                variant="flat"
                class="mx-4"
              >
                {{ searchResult.status }}
              </v-chip>
            </v-card-title>

            <v-divider class="my-3"></v-divider>

            <v-card-text class="text-body-1">
              <v-row dense>
                <v-col cols="4" class="text-grey-darken-1">ชื่อ-สกุล:</v-col>
                <v-col cols="8" class="font-weight-bold">{{
                  searchResult.fullName
                }}</v-col>

                <v-col cols="4" class="text-grey-darken-1 mt-2">ข้อความ:</v-col>
                <v-col cols="8" class="mt-2 text-pre-wrap">{{
                  searchResult.comment || "-"
                }}</v-col>
              </v-row>

              <v-alert
                v-if="
                  searchResult.status === 'Approved' ||
                  searchResult.status === 'Approve'
                "
                type="success"
                variant="tonal"
                class="mt-6"
                icon="mdi-check-circle"
              >
                เอกสารของคุณได้รับการอนุมัติเรียบร้อยแล้ว
              </v-alert>

              <v-alert
                v-else-if="searchResult.status === 'Rejected'"
                type="error"
                variant="tonal"
                class="mt-6"
                icon="mdi-alert-circle"
              >
                กรุณาติดต่อเจ้าหน้าที่เพื่อแก้ไขข้อมูล
              </v-alert>

              <v-alert
                v-else-if="searchResult.status === 'Pending'"
                type="info"
                variant="tonal"
                class="mt-6"
                icon="mdi-clock-outline"
              >
                กำลังอยู่ระหว่างการตรวจสอบเอกสาร
              </v-alert>
            </v-card-text>

            <v-card-actions>
              <v-btn
                block
                variant="text"
                color="grey-darken-2"
                prepend-icon="mdi-arrow-left"
                @click="resetForm"
              >
                กลับไปหน้าค้นหา
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-fade-transition>
      </v-col>
    </v-row>
    <v-btn
      class="mt-5 text-white"
      color="#EC407A"
      type="submit"
      to="/"
      size="large"
    >
      NPTU-PLO
    </v-btn>
  </v-container>
</template>

<script setup>
import { ref, reactive, getCurrentInstance } from "vue";
import { useAuthStore } from "@/stores/auth";
import axios from "axios";
import { useRouter } from "vue-router";

const router = useRouter();
const authStore = useAuthStore();

// Get SweetAlert instance
const { proxy } = getCurrentInstance();

const isLoading = ref(false);
const searchResult = ref(null); // If null, show login. If object, show result.

const form = reactive({
  studentNum: "",
  phone: "",
});

// Helper for Status Colors
const statusColor = (status) => {
  if (!status) return "grey";
  const s = status.toLowerCase();
  if (s.includes("approve") || s.includes("อนุมัติ")) return "success"; // Green
  if (s.includes("reject") || s.includes("แก้ไข")) return "error"; // Red
  return "warning"; // Orange (Pending)
};

const checkStatus = async () => {
  if (!form.studentNum || !form.phone) return;

  isLoading.value = true;
  try {
    // Call the backend route we just fixed
    const response = await axios.post("/api/check-status", {
      studentNum: form.studentNum,
      phone: form.phone,
    });

    if (response.data.found) {
      authStore.storeBackendJwt(response.data.token);
      searchResult.value = response.data.userInfo;
      if (searchResult.value) {
        authStore.user = { ...response.data.userInfo };
        authStore.user.Role = "alumni"; // Set role to alumni.
        authStore.user.displayName = response.data.userInfo.fullName;
        authStore.user.firstname = response.data.userInfo.firstname;
        authStore.user.lastname = response.data.userInfo.lastname;
        console.log("debug login response", authStore.user);
        console.log("JWTToken:", authStore.getBackendJwt());
        authStore.drawer = true;
        authStore.rail = true;
        authStore.loading = false;
      }
      router.push("/alumniDetail");

      // Optional: Quick success toast
      proxy.$swal({
        icon: "success",
        title: "พบข้อมูล",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 1500,
      });
    }
  } catch (error) {
    console.error(error);
    // Show error alert
    proxy.$swal({
      icon: "error",
      title: "เกิดข้อผิดพลาด",
      text:
        error.response?.data?.message ||
        "รหัสนักศึกษาหรือเบอร์โทรศัพท์ไม่ถูกต้อง",
      confirmButtonColor: "#d33",
    });
  } finally {
    isLoading.value = false;
  }
};

const resetForm = () => {
  searchResult.value = null;
  form.studentNum = "";
  form.phone = "";
};
</script>

<style scoped>
.text-pre-wrap {
  white-space: pre-wrap; /* Keeps line breaks from Google Sheets comments */
}
/* Border utility for the top of the card */
.border-success {
  border-top-color: #4caf50 !important;
}
.border-warning {
  border-top-color: #fb8c00 !important;
}
.border-error {
  border-top-color: #ff5252 !important;
}
</style>
